import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import BaseOption from "%/dynamic-component-editor/classes/base-option";
import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";
import DynamicComponent from "%/dynamic-component-editor/classes/dynamic-component";
import DynamicOption from "%/dynamic-component-editor/classes/dynamic-option";
import App from "@bloglovin/inspire-engine/App";
import OptionContextBinding from "%/dynamic-component-editor/classes/option-context-binding";
import OptionExpression from "@bloglovin/inspire-engine/Helpers/OptionExpression.js";
import { Context } from "%/dynamic-component-editor/classes/context-v2";
import { resolveContextEngineAppName } from "%/dynamic-component-editor/classes/platform-standard-entities";
import { readEntityFieldWithStoryMetadata as sharedReadEntityFieldWithStoryMetadata } from "%/dynamic-components/render-engine/helpers/read-entity-field-with-story-metadata";
import { getCompiledElement, hasCompiledElement, readCompiledOption } from "%/dynamic-components/render-engine/compiled-payload-store";
import {
  RATIO_SIZE_OPTION_KEYS, assertRenderableSizeValue, definitionSupportsRatioSize,
} from "%/dynamic-component-editor/options/ratio-size-support";
import { isVisitorThemeMode, readVisitorThemeMode }
  from "%/dynamic-components/render-engine/visitor-theme-mode";
import { renderSurfaceKind, isCompiledSurfaceKind } from "%/dynamic-component-editor/helpers/context-error-report";
import { expressionGlobalValues } from "%/dynamic-component-editor/helpers/expression-global-values";
import { readUnsetRefAsNull } from "%/dynamic-component-editor/helpers/unset-ref-read";

// eslint-disable-next-line global-require
const useDynamicComponentEditorStore = (...args) => require("%/dynamic-component-editor/stores/dynamic-component-editor").default(...args);

function assertNotCompiledRenderSurface(elementId, optionKey) {
  const data = typeof window !== "undefined" ? window.blclient?.data : null;
  if (data && (data.stagingCompiledDescriptor || data.productionCompiledDescriptor)) {
    throw new DynamicComponentError(
      "compiled render surface read live builder data — element missing from the compiled payload; "
        + "the compile/publish is incomplete",
      { elementId, optionKey },
    );
  }
}

const OptionCalculationMixin = {
  props: {
    optionValues: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    activeState: {
      type: String,
      default: "default",
      required: false,
    },
    identifier: {
      type: String,
      required: false,
      default: "",
    },
    themeMode: {
      type: String,
      required: false,
      default: "",
    },
  },
  inject: {
    // Expressions on any element inside an embedded component read that component's contexts.
    scopedProvidedContexts: {
      from: "scopedProvidedContexts",
      default: null,
    },
    variableOverridesForCurrentDC: {
      from: "variableOverridesForCurrentDC",
      default: null,
    },
    renderingInProduction: {
      from: "renderingInProduction",
      default: false,
    },
  },
  computed: {
    resolvedThemeMode() {
      if (isVisitorThemeMode(this.themeMode)) {
        return this.themeMode;
      }
      return readVisitorThemeMode();
    },
    dynamicElement() {
      return this.identifier ? DynamicElement.get(this.identifier) : null;
    },
    dynamicComponent() {
      return DynamicComponent.get(this.dynamicElement?.dynamicComponentId);
    },
    currentDcThemeId() {
      return this.dynamicComponent?.themeId;
    },
    theme() {
      return this.dynamicComponent?.theme;
    },
  },
  methods: {
    getOptionKey(optionClass) {
      if (!optionClass) {
        throw new DynamicComponentError("Option class is required", {});
      }
      const optionKey = optionClass.optionKey;
      if (!optionKey) {
        throw new DynamicComponentError("Option class is missing optionKey", {
          elementName: this.$options.name,
          optionClass,
        });
      }

      return optionKey;
    },
    migrateOptionValue(value, key) {
      switch (key) {
        case "margins":
        case "padding":
          if (typeof value === "string") {
            const [top, right, bottom, left] = value.split(" ");
            return { top, right, bottom, left };
          }
          if (value && typeof value === "object") {
            value.top = value.top ?? "0px";
            value.right = value.right ?? "0px";
            value.bottom = value.bottom ?? "0px";
            value.left = value.left ?? "0px";
            return value;
          }
          return { top: "0px", right: "0px", bottom: "0px", left: "0px" };
        case "box-shadow":
        case "background-gradient":
        case "text-shadow":
        case "background-color":
          if (!value) {
            return "none";
          }
          return value;
        case "border-radius":
          if (!value) {
            return "0px";
          }
          return value;
        default:
          return value;
      }
    },
    // eslint-disable-next-line no-unused-vars
    getOptionValue(optionClass, state = BaseOption.$states.DEFAULT, property = "") {
      const [stateFromActive, breakpoint = "default"] = this.activeState.split(":");
      return this.readOptionValueAt(optionClass, stateFromActive, breakpoint, property);
    },
    getOptionValueAtState(optionClass, optionState, property = "") {
      const [, breakpoint = "default"] = this.activeState.split(":");
      return this.readOptionValueAt(optionClass, optionState, breakpoint, property);
    },
    readOptionValueAt(optionClass, optionState, breakpoint, property) {
      const value = this.resolveOptionValueAt(optionClass, optionState, breakpoint, property);
      if (!property) {
        this.assertRenderableSize(optionClass.optionKey, value);
      }
      return value;
    },
    elementTypeForOptionChecks() {
      return this.dynamicElement?.type ?? getCompiledElement(this.identifier)?.type ?? this.$options?.name ?? "";
    },
    assertRenderableSize(optionKey, value) {
      if (!RATIO_SIZE_OPTION_KEYS.includes(optionKey)) {
        return;
      }
      const type = this.elementTypeForOptionChecks();
      assertRenderableSizeValue({
        optionKey,
        value,
        identifier: this.identifier,
        type,
        supportsRatioSize: DynamicElement.supportsRatioSize(type) || definitionSupportsRatioSize(this.$options),
      });
    },
    resolveOptionValueAt(optionClass, optionState, breakpoint, property) {
      const optionKey = this.getOptionKey(optionClass);
      const hasProvidedOptionValues = this.optionValues && Object.keys(this.optionValues).length > 0;
      const overridesProvider = this.variableOverridesForCurrentDC ?? null;
      const overrides = typeof overridesProvider === "function"
        ? overridesProvider(breakpoint, optionState)
        : overridesProvider;

      let compiledValue = this._resolveVariableBoundValue(optionKey, overrides);

      if (compiledValue === undefined) {
        if (hasCompiledElement(this.identifier)) {
          compiledValue = readCompiledOption(this.identifier, optionKey, this.resolvedThemeMode, breakpoint, optionState);
        } else if (this.dynamicElement) {
          assertNotCompiledRenderSurface(this.identifier, optionKey);
          compiledValue = this.dynamicElement.getCompiledOptionValue(
            optionKey, breakpoint, optionState, this.resolvedThemeMode, overrides,
          );
        } else if (hasProvidedOptionValues) {
          const dynamicElement = this.dynamicElement;
          compiledValue = DynamicOption.getCompiledOptionValue(
            {
              type: dynamicElement?.type,
              elementType: dynamicElement?.elementType,
              brandedType: dynamicElement?.brandedType,
              brandedVariant: dynamicElement?.brandedVariant,
              brandedSize: dynamicElement?.brandedSize,
              dynamicComponentId: dynamicElement?.dynamicComponentId,
              destinationId: dynamicElement?.destinationId ?? null,
              options: this.optionValues,
            },
            optionKey,
            breakpoint,
            optionState,
            {
              themeMode: this.resolvedThemeMode,
              themeId: this.currentDcThemeId,
              theme: this.theme,
              dynamicComponent: this.dynamicComponent,
              breakpoints: this.dynamicComponent?.breakpoints ?? [],
            },
          );
        }
      }

      if (OptionExpression.isToken(compiledValue)) {
        const evaluated = OptionExpression.resolve(compiledValue, {
          contexts: this.contextBindingContexts(),
          readEntityField: this.readEntityFieldWithStoryMetadata,
        });
        if (evaluated === undefined) {
          return undefined;
        }
        const projected = typeof optionClass?.projectBoundValue === "function"
          ? optionClass.projectBoundValue(evaluated)
          : evaluated;
        return property ? projected?.[property] : projected;
      }

      if (OptionContextBinding.isToken(compiledValue)) {
        const bound = OptionContextBinding.resolve(compiledValue, {
          contexts: this.contextBindingContexts(),
          readEntityField: this.readEntityFieldWithStoryMetadata,
        });
        if (bound === undefined) {
          return undefined;
        }
        const projected = typeof optionClass?.projectBoundValue === "function"
          ? optionClass.projectBoundValue(bound)
          : bound;
        return property ? projected?.[property] : projected;
      }

      const migrated = this.migrateOptionValue(compiledValue, optionKey);
      if (!property) {
        return migrated;
      }
      return migrated?.[property];
    },
    contextBindingContexts() {
      const scoped = typeof this.scopedProvidedContexts === "function"
        ? this.scopedProvidedContexts()
        : null;
      return {
        ...Context.allContexts,
        ...this.builderPrimitiveValueContexts(),
        ...Context.allValueContexts,
        ...scoped ?? {},
        ...expressionGlobalValues(),
      };
    },
    builderPrimitiveValueContexts() {
      if (isCompiledSurfaceKind(renderSurfaceKind())) {
        return {};
      }
      return useDynamicComponentEditorStore().builderPrimitiveValueContexts;
    },
    resolveContextBindingScope() {
      return {
        contexts: this.contextBindingContexts(),
        readEntityField: this.readEntityFieldWithStoryMetadata,
      };
    },
    readEntityFieldWithStoryMetadata(entityType, pk, fieldName, appName) {
      return readUnsetRefAsNull(() => sharedReadEntityFieldWithStoryMetadata(
        App.get(resolveContextEngineAppName(appName)), entityType, pk, fieldName,
      ))();
    },
    _resolveVariableBoundValue(optionKey, overrides) {
      const dynamicElement = this.dynamicElement;
      const dynamicComponent = this.dynamicComponent;
      if (!dynamicElement || !dynamicComponent) {
        return undefined;
      }
      const variableId = dynamicComponent.getVariableBindingForOption?.(dynamicElement.id, optionKey) ?? null;
      if (variableId === null || variableId === undefined) {
        return undefined;
      }
      const override = overrides?.[variableId];
      const raw = override !== undefined ? override : dynamicComponent.getVariableValue(variableId);
      if (raw === undefined) {
        return undefined;
      }
      const optionClassResolved = DynamicElement.getOptionClass(dynamicElement.type, optionKey);
      if (!optionClassResolved) {
        return undefined;
      }
      try {
        return optionClassResolved.constructor.convertConfigValueToCompiledValue(raw, this.currentDcThemeId, this.resolvedThemeMode);
      } catch {
        return undefined;
      }
    },
  },
};

export { OptionCalculationMixin };
